import React, { useState, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Autocomplete, Box, Button, CardActions, CardContent, Grid, Paper, Typography } from '@availity/element';

import { AvailityHeaderLogo, Footer } from '@/components';
import { useAppContext } from '@/context';
import {
  EmailSent,
  EnterUserId,
  LoginStart,
  NearExpiredPassword,
  PasswordReset,
  PasswordResetSuccess,
  TwoFAAuthenticatorInputToken,
  TwoFAAuthenticatorSetup,
  TwoFAChooseMethod,
  TwoFAComplete,
  TwoFACompleteWarning,
  TwoFAInputBackupCodeV2,
  TwoFAInputToken,
  TwoFAWelcome,
} from '@/pages';

// all mock json imported below
import * as loginStartJson from '../../data/journeys/login/login-01.json';
import * as emailSentJson from '../../data/email-sent.json';
import * as enterUserIdJson from '../../data/journeys/reset-password/reset-password-01.json';
import * as nearExpiredPasswordJson from '../../data/journeys/login/login-almost-expired-pwd.json';
import * as passwordResetJson from '../../data/journeys/reset-password/reset-password-02.json';
import * as passwordResetSuccessJson from '../../data/journeys/reset-password/reset-password-login-success.json';
import * as oathAppRegisterJson from '../../data/journeys/2FAv2/2FAv2-02.json';
import * as oathAppVerifyJson from '../../data/journeys/2FAv2/2FAv2-03.json';
import * as twoFAChooseMethodJson from '../../data/journeys/2FA/challenge/2fa-challenge-00.json';
import * as twoFACompleteJson from '../../data/journeys/2FAv2/2FAv2-04.json';
import * as twoFACompleteNagJson from '../../data/journeys/2FAv2/2FAv2-04-nag.json';
import * as twoFACompleteWithCodesJson from '../../data/journeys/2FAv2/2FAv2-04-backup-codes.json';
import * as twoFACompleteWithCodesNagJson from '../../data/journeys/2FAv2/2FAv2-04-backup-codes-nag.json';
import * as twoFAInputBackupCodesJson from '../../data/journeys/2FA/challenge/2fa-challenge-backupcode-01.json';
import * as twoFAInputTokenSmsJson from '../../data/journeys/2FA/challenge/2fa-challenge-01-sms.json';
import * as twoFAInputTokenVoiceJson from '../../data/journeys/2FA/challenge/2fa-challenge-01-voice.json';
import * as twoFAWelcomeJson from '../../data/journeys/2FA/enrollment/2fa-enrollment-00.json';

// page names as options for the select component
const pageNames = [
  '2FA Authenticator Enrollment Page',
  '2FA Authenticator Input Token Page',
  '2FA Choose Method Page',
  '2FA Complete With Codes Page',
  '2FA Complete Page',
  '2FA Complete Warning Page (w/ Codes)',
  '2FA Complete Warning Page',
  '2FA Input Backup Code Page',
  '2FA Input Token Page (SMS)',
  '2FA Input Token Page (Voice)',
  '2FA Welcome Page',
  'Email Sent Page',
  'Enter User ID Page',
  'Login Page',
  'Near Expired Password Page',
  'Password Reset Page',
  'Password Reset Success Page',
];

function PageTest() {
  const { setQueryParams } = useAppContext();
  const [stepToRender, setCurrentStep] = useState();

  // setting some basic query params needed for certain pages
  useEffect(() => {
    // setQueryParams({
    //   userid: 'testuserid',
    // });
  }, []);

  // initialize form
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm({
    defaultValues: {
      password: '',
    },
  });

  // load the page to display based off of the mock data in stepToRender
  const component = useMemo(() => {
    let output;

    if (stepToRender) {
      const { payload = {} } = stepToRender;
      const { stage, callbacks = [] } = payload;

      switch (stage) {
        case 'LoginpageUnique001': {
          output = <LoginStart stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'ForgottenPassword001': {
          output = <EnterUserId stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'ForgottenPassword002': {
          setQueryParams({
            userid: 'testuserid',
          });

          output = <PasswordReset stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'ForgottenPassword003': {
          output = <PasswordResetSuccess stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'LoginExpiringPassword': {
          output = <NearExpiredPassword stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'MFA-BackupVerify': {
          output = <TwoFAInputBackupCodeV2 stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'MFAEnrollment-OATHAppRegister': {
          output = <TwoFAAuthenticatorSetup stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'MFAEnrollment-AppOATHVerify': {
          output = <TwoFAAuthenticatorInputToken stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'NAG-MFA-CompleteWithCodes': {
          output = <TwoFACompleteWarning stepToRender={stepToRender} setCurrentStep={setCurrentStep} showBackupCodes />;
          break;
        }
        case 'MFAEnrollment-CompleteWithCodes': {
          output = <TwoFAComplete stepToRender={stepToRender} setCurrentStep={setCurrentStep} showBackupCodes />;
          break;
        }
        case 'NAG-MFA-CompleteWithoutCodes': {
          output = (
            <TwoFACompleteWarning stepToRender={stepToRender} setCurrentStep={setCurrentStep} showBackupCodes={false} />
          );
          break;
        }
        case 'MFA-CompleteWithoutCodes': {
          output = (
            <TwoFAComplete stepToRender={stepToRender} setCurrentStep={setCurrentStep} showBackupCodes={false} />
          );
          break;
        }
        case 'MFA-SelectMethod': {
          output = <TwoFAChooseMethod stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'MFA-SMSOTPVerify': {
          output = <TwoFAInputToken stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'MFAEnrollment-Welcome': {
          output = <TwoFAWelcome stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        default:
      }

      // email sent page doesn't key off of stage name, but instead off of a suspended text output callback
      if (callbacks[0]?.type?.toLowerCase() === 'suspendedtextoutputcallback') {
        output = <EmailSent stepToRender={stepToRender} />;
      }
    }

    return output;
  }, [stepToRender, setQueryParams]);

  // on submit, set the mock data as the stepToRender based on the page selected
  const onSubmit = (values) => {
    const { page } = values;
    let newStep;

    switch (page) {
      case '2FA Authenticator Enrollment Page': {
        newStep = { payload: { ...oathAppRegisterJson } };
        break;
      }
      case '2FA Authenticator Input Token Page': {
        newStep = { payload: { ...oathAppVerifyJson } };
        break;
      }
      case '2FA Choose Method Page': {
        newStep = { payload: { ...twoFAChooseMethodJson } };
        break;
      }
      case '2FA Complete With Codes Page': {
        newStep = { payload: { ...twoFACompleteWithCodesJson } };
        break;
      }
      case '2FA Complete Warning Page (w/ Codes)': {
        newStep = { payload: { ...twoFACompleteWithCodesNagJson } };
        break;
      }
      case '2FA Complete Page': {
        newStep = { payload: { ...twoFACompleteJson } };
        break;
      }
      case '2FA Complete Warning Page': {
        newStep = { payload: { ...twoFACompleteNagJson } };
        break;
      }
      case '2FA Input Backup Code Page': {
        newStep = { payload: { ...twoFAInputBackupCodesJson } };
        break;
      }
      case '2FA Input Token Page (SMS)': {
        newStep = { payload: { ...twoFAInputTokenSmsJson } };
        break;
      }
      case '2FA Input Token Page (Voice)': {
        newStep = { payload: { ...twoFAInputTokenVoiceJson } };
        break;
      }
      case '2FA Welcome Page': {
        newStep = { payload: { ...twoFAWelcomeJson } };
        break;
      }
      case 'Enter User ID Page': {
        newStep = { payload: { ...enterUserIdJson } };
        break;
      }
      case 'Email Sent Page': {
        newStep = { payload: { ...emailSentJson } };
        break;
      }
      case 'Login Page': {
        newStep = { payload: { ...loginStartJson } };
        break;
      }
      case 'Near Expired Password Page': {
        newStep = { payload: { ...nearExpiredPasswordJson } };
        break;
      }
      case 'Password Reset Page': {
        newStep = { payload: { ...passwordResetJson } };
        break;
      }
      case 'Password Reset Success Page': {
        newStep = { payload: { ...passwordResetSuccessJson } };
        break;
      }
      default:
    }

    setCurrentStep(newStep);
  };

  return stepToRender ? (
    component
  ) : (
    <Grid
      id="page-test-grid-container"
      container
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      <Grid
        size={{ xs: 12, sm: 12, md: 6 }}
        display="flex"
        flexDirection="column"
        alignItems="center"
        flexGrow={1}
        sx={{ minHeight: '100vh' }}
      >
        <AvailityHeaderLogo />
        <Grid direction="column" alignItems="center" justifyContent="center">
          <Paper variant="elevation" elevation={6} sx={{ width: { xs: '350px', sm: '500px', md: '500px' } }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <CardContent>
                <Typography variant="h1" mb={2}>
                  Page Layout Tester
                </Typography>
                <Typography variant="subtitle1" mb={2}>
                  Select a page name to quickly test it&apos;s layout. Note that selecting any navigation buttons on any
                  of the pages will result in error as each page is now loaded in isolation without the context of
                  it&apos;s journey.
                </Typography>
                <Typography variant="subtitle1" mb={3}>
                  <Box component="span" sx={{ fontWeight: 'bold' }}>
                    To return here, simply reload the page.
                  </Box>
                </Typography>
                <Box>
                  <Controller
                    name="page"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <Autocomplete
                        id="page"
                        onChange={(_, value, reason) => {
                          if (reason === 'clear') {
                            onChange(null);
                          }
                          onChange(value);
                        }}
                        value={value || null}
                        onBlur={onBlur}
                        FieldProps={{
                          id: 'page',
                          label: 'Page Name',
                          placeholder: 'Select...',
                          error: !!errors?.stage,
                          helperText: errors?.stage?.message,
                          sx: { marginBottom: 0 },
                          fullWidth: true,
                        }}
                        options={pageNames}
                      />
                    )}
                    {...register('page', {
                      required: 'Select one.',
                    })}
                  />
                </Box>
              </CardContent>
              <CardActions sx={{ justifyContent: 'end' }}>
                <Button color="primary" type="submit">
                  Submit
                </Button>
              </CardActions>
            </form>
          </Paper>
        </Grid>
        <Footer />
      </Grid>
    </Grid>
  );
}

export default PageTest;
